import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, useTheme, useMediaQuery, Button, Menu, MenuItem, IconButton, Divider, ListItemIcon } from '@mui/material';
import { AccountCircle, Dashboard as DashboardIcon, Tag as TagIcon, Business as BusinessIcon, Assessment as AssessmentIcon, Lightbulb as LightbulbIcon, Group as GroupIcon, Feedback as FeedbackIcon } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSimulation } from '../../contexts/SimulationContext';
import { useAuth, Organization } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import OrganizationSwitchModal from '../OrganizationSwitchModal';
import WaveScoopLogoWhite from '../../assets/images/WaveScoopLogoWhite.jsx';
import { useFeedbackContext } from '../../contexts/FeedbackContext';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { dispatch } = useSimulation();
  const location = useLocation();
  const { isAuthenticated, user, logout, getUserOrganizations } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const navigate = useNavigate();
  const { openFeedbackModal } = useFeedbackContext();
  
  // Mobile menu state
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleHeaderClick = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleOrganizationsClick = async () => {
    handleMenuClose();
    try {
      const orgs = await getUserOrganizations();
      setOrganizations(orgs);
      setShowOrgModal(true);
    } catch (error) {
      logger.error('Failed to fetch organizations:', error);
    }
  };
  
  const handleFeedbackClick = () => {
    handleMenuClose();
    openFeedbackModal();
  };
  
  // Mobile menu handlers
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };
  
  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  return (
    <>
      <AppBar 
        position="static" 
        color="primary"
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#07485a' : theme.palette.primary.main,
          backgroundImage: 'none'
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Box 
              component={Link} 
              to="/dashboard"
              onClick={handleHeaderClick}
              sx={{
                color: 'white', 
                textDecoration: 'none',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'Sora', // Matching PublicHeader for consistency
              }}
            >
              <Box
                sx={{ 
                  width: isMobile ? 20 : 30, 
                  height: isMobile ? 20 : 30, 
                  marginRight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <WaveScoopLogoWhite 
                  width="100%"
                  height="100%"
                />
              </Box>
              <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                WaveScoop
              </Typography>
            </Box>
          </Box>
          
          {/* Navigation Buttons - Desktop */}
          <Box component="nav" sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {isAuthenticated && (
              <>
                <Button 
                  component={Link} 
                  to="/dashboard" 
                  color="inherit"
                  variant="contained"
                  sx={{ 
                    bgcolor: location.pathname === '/dashboard' ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                  startIcon={<DashboardIcon />}
                >
                  Dashboard
                </Button>
                
                <Button 
                  component={Link} 
                  to="/simulations" 
                  color="inherit"
                  variant="contained"
                  startIcon={<AssessmentIcon />}
                  sx={{ 
                    bgcolor: location.pathname === '/simulations' ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                >
                  Simulations
                </Button>

                <Button 
                  component={Link} 
                  to="/ideas" 
                  color="inherit"
                  variant="contained"
                  sx={{ 
                    bgcolor: location.pathname === '/ideas' ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                  startIcon={<LightbulbIcon />}
                >
                  Ideas
                </Button>

                <Button 
                  component={Link} 
                  to="/personas" 
                  color="inherit"
                  variant="contained"
                  sx={{ 
                    bgcolor: location.pathname === '/personas' ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                  startIcon={<GroupIcon />}
                >
                  Personas
                </Button>

                <Button 
                  component={Link} 
                  to="/accounts" 
                  color="inherit"
                  variant="contained"
                  sx={{ 
                    bgcolor: location.pathname === '/accounts' ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                  startIcon={<TagIcon />}
                >
                  Social Accounts
                </Button>
              </>
            )}

            {/* User Info - moved to the right of navigation buttons */}
            {isAuthenticated && user && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 3, mr: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white',
                    display: { xs: 'none', md: 'block' },
                    alignSelf: 'center'
                  }}
                >
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user.email} • {user.organizationName || 'Organization'}
                </Typography>
              </Box>
            )}
            
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                edge="end"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </IconButton>
            )}
            
            {isAuthenticated ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenuOpen}
                  edge="end"
                >
                  <AccountCircle />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem disabled>
                    {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email}
                  </MenuItem>
                  <MenuItem onClick={handleSettingsClick}>Settings</MenuItem>
                  <MenuItem onClick={handleOrganizationsClick}>
                    {/* <ListItemIcon>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon> */}
                    Other Organizations
                  </MenuItem>
                  <MenuItem onClick={handleFeedbackClick}>
                    Send Feedback
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <Button 
                component={Link} 
                to="/login?source=header" 
                color="inherit"
                variant="outlined"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="mobile-menu"
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        {!isAuthenticated && (
          <></>
        )}
        {isAuthenticated && (
          <>
            <Divider />
            <MenuItem 
              component={Link} 
              to="/dashboard"
              onClick={handleMobileMenuClose}
            >
              Dashboard
            </MenuItem>
            <MenuItem 
              component={Link} 
              to="/simulations"
              onClick={handleMobileMenuClose}
            >
              {/* <ListItemIcon>
                <AssessmentIcon />
              </ListItemIcon> */}
              Simulations
            </MenuItem>
            <MenuItem 
              component={Link} 
              to="/ideas"
              onClick={handleMobileMenuClose}
            >
              Ideas
            </MenuItem>
            <MenuItem component={Link} to="/personas" onClick={handleMobileMenuClose}>Personas</MenuItem>
            <MenuItem component={Link} to="/accounts" onClick={handleMobileMenuClose}>Social Accounts</MenuItem>
          </>
        )}
        {!isAuthenticated && (
          <>
            <Divider />
            <MenuItem 
              component={Link} 
              to="/login?source=header"
              onClick={handleMobileMenuClose}
            >
              Login
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem 
          component={Link} 
          to="/legal/privacy-policy"
          onClick={handleMobileMenuClose}
        >
          Privacy Policy
        </MenuItem>
        <MenuItem 
          component={Link} 
          to="/legal/terms-of-service"
          onClick={handleMobileMenuClose}
        >
          Terms of Service
        </MenuItem>
      </Menu>
      
      <OrganizationSwitchModal
        open={showOrgModal}
        onClose={() => setShowOrgModal(false)}
        organizations={organizations}
      />
    </>
  );
};

export default Header; 
